// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useTheme } from '../utils/ThemeContext';

import './Login.css';

/**
 * Login page component with Google and passwordless authentication
 * using React Bootstrap components and theme support
 * 
 * @returns {JSX.Element} Rendered login page
 */
const Login = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check for email link sign-in when component mounts
  useEffect(() => {
    // Check if the URL contains a sign-in link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Get the email from localStorage (saved when the link was sent)
      let email = localStorage.getItem('emailForSignIn');
      
      if (!email) {
        // If email isn't found in localStorage, ask the user for it
        email = window.prompt('Please provide your email for confirmation');
      }
      
      if (email) {
        setLoading(true);
        
        // Complete the sign-in process
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            // Clear the email from localStorage
            localStorage.removeItem('emailForSignIn');
            
            // Navigate to the home page
            navigate('/');
          })
          .catch((error) => {
            console.error("Error signing in with email link:", error);
            setError(getErrorMessage(error));
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [navigate]);

  /**
   * Sign in with Google authentication
   * 
   * @returns {Promise<void>}
   */
  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
      navigate('/');
    } catch (error) {
      console.error("Error signing in with Google:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send magic link to email for passwordless sign-in
   * 
   * @param {Event} e - Form submission event
   * @returns {Promise<void>}
   */
  const handleEmailLinkSignIn = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setLoading(true);
      
      // URL to redirect back to after clicking the link
      const actionCodeSettings = {
        // URL you want to redirect back to after sign-in
        url: `${window.location.origin}/login`,
        // This must be true for email link sign-in
        handleCodeInApp: true
      };
      
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Save the email in localStorage so we can access it when the user clicks the link
      localStorage.setItem('emailForSignIn', email);
      
      // Clear error and set success message
      setError('');
      setMessage(`Magic link sent to ${email}. Check your inbox to sign in.`);
    } catch (error) {
      console.error("Error sending sign-in link:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get user-friendly error message from Firebase error
   * 
   * @param {Error} error - Firebase auth error
   * @returns {string} User-friendly error message
   */
  const getErrorMessage = (error) => {
    const errorCode = error.code;
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/expired-action-code':
        return 'The sign-in link has expired. Please request a new one.';
      case 'auth/invalid-action-code':
        return 'The sign-in link is invalid. It may have been used already.';
      case 'auth/missing-email':
        return 'Please provide a valid email address.';
      default:
        return 'An error occurred during authentication. Please try again.';
    }
  };

  return (
    <Container fluid className="p-0 login-container">
      {/* Hero Section with Authentication */}
      <Row className="g-0">
        <Col md={7} className="bg-image-container d-flex align-items-center justify-content-center">
          <div className="hero-content text-center text-md-start p-4 p-md-5">
            <h1 className="display-4 fw-semi-bold mb-4">Motorcycle Trip Management Suite</h1>
            <p className="lead mb-5">Tools to make your motorcycle adventures simpler and more organised</p>
            
            <div className="d-flex justify-content-end mt-3">
              <Button 
                variant={theme === 'light' ? 'outline-dark' : 'outline-light'}
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                <i className={`bi ${theme === 'light' ? 'bi-moon' : 'bi-sun'}`}></i>
                {theme === 'light' ? ' Dark Mode' : ' Light Mode'}
              </Button>
            </div>
          </div>
        </Col>

        <Col md={5} className="login-form-container">
          <Card className="border-0 h-100">
            <Card.Body className="d-flex flex-column justify-content-center p-4 p-lg-5">
              <div className="text-center mb-4">
                <h2>Sign In</h2>
                <p className="text-muted">Access your motorcycle trip tools</p>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}
              {message && <Alert variant="success">{message}</Alert>}

              <div className="auth-methods">
                {/* Google Sign In */}
                <Button 
                  variant="outline-primary" 
                  className="w-100 mb-4 d-flex align-items-center justify-content-center" 
                  onClick={signInWithGoogle}
                  disabled={loading}
                >
                  <i className="bi bi-google me-2"></i>
                  {loading ? 'Signing in...' : 'Sign in with Google'}
                </Button>

                <div className="separator text-muted text-center mb-4">
                  <span className="separator-text">OR</span>
                </div>

                {/* Email Link Sign In */}
                <Form onSubmit={handleEmailLinkSignIn}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      We'll send a magic link to your email. Click the link to sign in without a password.
                    </Form.Text>
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-100" 
                    disabled={loading}
                  >
                    {loading ? 'Sending Link...' : 'Send Magic Link'}
                  </Button>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Features Section */}
      <Row className="g-0 py-5 features-section">
        <Col xs={12} className="text-center mb-5">
          <h2 className="fw-semi-bold">Our Features</h2>
          <p className="section-subtitle">Everything you need for your motorcycle journeys</p>
        </Col>
        
        <Container>
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center light-mode-card">
                <Card.Img variant="top" src="/images/packing-list-200px.jpg" alt="Packing Lists" />
                <Card.Body className="py-3 px-3">
                  <Card.Title>Packing Lists</Card.Title>
                  <Card.Text>
                    Create detailed packing lists for different trips. Track items, quantities, and where they're stored on your motorcycle.
                  </Card.Text>
                  <ul className="list-unstyled mt-3">
                    <li><i className="bi bi-check2 text-primary me-2"></i>Create and share packing lists</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Track item locations</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Save different lists for different trips</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center light-mode-card">
                <Card.Img variant="top" src="/images/maintenance-list-200px.jpg" alt="Maintenance Lists" />
                <Card.Body className="py-3 px-3">
                  <Card.Title>Maintenance Lists</Card.Title>
                  <Card.Text>
                    Keep track of your motorcycle's maintenance history, costs, and upcoming service needs.
                  </Card.Text>
                  <ul className="list-unstyled mt-3">
                    <li><i className="bi bi-check2 text-primary me-2"></i>Track service intervals</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Log maintenance costs</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Set reminders for upcoming service needs</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm text-center light-mode-card">
                <Card.Img variant="top" src="/images/coming-soon-200px.jpg" alt="Maintenance Lists" />
                <Card.Body className="py-3 px-3">
                  <Card.Title>Coming Soon: You Decide</Card.Title>
                  <Card.Text>
                    What do you want to see next?
                  </Card.Text>
                  <ul className="list-unstyled mt-3">
                    <li><i className="bi bi-check2 text-primary me-2"></i>Feature coming soon</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Feature coming soon</li>
                    <li><i className="bi bi-check2 text-primary me-2"></i>Feature coming soon</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Row>

      {/* CTA Section */}
      <Row className="g-0 py-5 cta-section text-center">
        <Col xs={12}>
          <h2 className="mb-4">Ready to Simplify Your Motorcycle Adventures?</h2>
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
            <Button 
              variant="outline-primary" 
              size="lg"
              onClick={signInWithGoogle}
            >
              <i className="bi bi-google me-2"></i>
              Sign in with Google
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;